import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bot, Brain, Clock, Pencil, Settings2, Wrench } from 'lucide-react';
import { useContextHook } from 'use-context-hook';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '../../Context/authContext';
import agentService from '../../services/agentService';
import toolsService from '../../services/toolsService';
import memoryService from '../../services/memoryService';
import { formatDate } from '../../helpers/common';
import Loaders from '../Loaders';
import cardIcon from '../../assets/feeda-icon.svg';

function resolveIconSrc(icon) {
  if (!icon || typeof icon !== 'string') return null;
  if (icon.startsWith('http://') || icon.startsWith('https://')) {
    return icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : icon;
  }
  if (icon.startsWith('data:') || icon.startsWith('/')) return icon;
  return `data:image/png;base64,${icon}`;
}

const AgentProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: agentData, isFetching } = useQuery({
    queryKey: ['get-single-agent', id],
    queryFn: () => agentService.getAgentDetail(id),
    enabled: !!id,
  });
  const detail = agentData?.detail;

  const { fetch } = useContextHook(AuthContext, v => ({ fetch: v.fetch }));
  const { _data: memoryData } = memoryService.GetMemoryList(id, fetch);
  const memories = memoryData?.results?.results || [];

  const { data: userToolsData } = useQuery({
    queryKey: ['tools', 'user_created'],
    queryFn: () => toolsService.getAllTools('user_created'),
    staleTime: Infinity,
  });
  const { data: adminToolsData } = useQuery({
    queryKey: ['tools', 'admin_created'],
    queryFn: () => toolsService.getAllTools('admin_created'),
    staleTime: Infinity,
  });

  const allTools = useMemo(
    () => [...(userToolsData?.tools || []), ...(adminToolsData?.tools || [])],
    [userToolsData, adminToolsData],
  );

  const agentTools = useMemo(
    () => allTools.filter(tool => detail?.tools?.includes(tool?.tool_id)),
    [allTools, detail],
  );

  if (isFetching) {
    return <Loaders pageLoader />;
  }

  const modelId = detail?.model_id || '';
  const provider = modelId.includes('gpt') ? 'OpenAI' : modelId.split('/')[0] || '—';
  const responseModel = modelId.includes('gpt') ? modelId : modelId.split('/')[1] || modelId;
  const iconSrc = resolveIconSrc(detail?.icon) || cardIcon;
  const memoryEnabled = detail?.enable_user_memories;

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <button
        type="button"
        onClick={() => navigate('/agents')}
        className="flex items-center gap-2 text-sm text-mute hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Agents
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start gap-5 mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
          {iconSrc ? (
            <img src={iconSrc} alt={detail?.agent_name} className="w-full h-full object-cover" />
          ) : (
            <Bot className="w-8 h-8 text-primary" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-ink mb-2 font-serif tracking-tight">{detail?.agent_name}</h1>
          {detail?.description && <p className="text-mute mb-3 max-w-2xl">{detail.description}</p>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-mute">
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Agent ID
            </span>
            <span className="font-mono text-mute break-all">{id}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/edit-agent/${id}`)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-shell-border text-sm text-ink hover:border-ink/20 flex-shrink-0">
          <Pencil className="w-4 h-4" /> Edit
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-shell-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Settings2 className="w-5 h-5 text-primary" />
              <h3 className="text-sm font-semibold text-ink">Instructions</h3>
            </div>
            <p
              className="text-sm text-mute leading-relaxed whitespace-pre-line"
              dangerouslySetInnerHTML={{
                __html: detail?.instructions?.replace(/\r\n/g, '<br>') || 'No instructions provided.',
              }}
            />
          </div>

          {memoryEnabled && memories.length > 0 && (
            <div className="bg-white border border-shell-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-primary" />
                  <h3 className="text-sm font-semibold text-ink">Memory</h3>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-paper text-mute">
                  {memories.length} items
                </span>
              </div>
              <div className="space-y-3">
                {memories.slice(0, 3).map((data, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between gap-3 p-3 rounded-lg bg-paper border border-shell-border">
                    <div className="min-w-0">
                      <p className="text-sm text-ink break-words">{data?.memory}</p>
                      <p className="text-xs text-mute mt-1">{formatDate(data?.created_at)}</p>
                    </div>
                    {data?.categories?.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0 justify-end max-w-[50%]">
                        {data.categories.map((item, i) => (
                          <span
                            key={i}
                            className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-ink border border-primary/20">
                            {item}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-shell-border rounded-xl p-5">
            <h3 className="text-sm font-semibold text-ink mb-4">Configuration</h3>
            <div className="divide-y divide-shell-border">
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-mute">Provider</span>
                <span className="text-sm text-ink font-medium">{provider}</span>
              </div>
              <div className="flex items-center justify-between py-2.5 gap-3">
                <span className="text-sm text-mute">Response Model</span>
                <span className="text-sm text-ink font-medium text-right break-all">{responseModel}</span>
              </div>
              <div className="flex items-center justify-between py-2.5">
                <span className="text-sm text-mute">Memory</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full border ${
                    memoryEnabled
                      ? 'bg-primary/10 text-ink border-primary/20'
                      : 'bg-paper text-mute border-shell-border'
                  }`}>
                  {memoryEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {agentTools.length > 0 && (
            <div className="bg-white border border-shell-border rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Wrench className="w-5 h-5 text-primary" />
                <h3 className="text-sm font-semibold text-ink">Tools & Integrations</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {agentTools.map((data, index) => (
                  <span
                    key={index}
                    className="px-2.5 py-1 rounded-full text-xs bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">
                    {data?.tool_name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentProfile;
